import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import instance from "../apiInstance";

const SESSION_KEY = "tracker_session_id";
const HEARTBEAT_INTERVAL = 60_000; // 1 min
const BATCH_FLUSH_INTERVAL = 15_000; // 15s

function generateSessionId() {
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function getDeviceInfo() {
  const ua = navigator.userAgent || "";
  let device_type = "Web";
  if (/iPhone|iPad|iPod/i.test(ua)) device_type = "iOS";
  else if (/Android/i.test(ua)) device_type = "Android";

  return {
    device_type,
    user_agent: ua,
    screen_width: window.screen?.width,
    screen_height: window.screen?.height,
    language: navigator.language,
    platform: navigator.platform,
    is_pwa: window.matchMedia("(display-mode: standalone)").matches,
  };
}

function getOrCreateSession() {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = generateSessionId();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

export default function useTracker() {
  const location = useLocation();
  const token = useSelector((state) => state.auth.token);
  const sessionId = useRef(getOrCreateSession());
  const sessionStart = useRef(Date.now());
  const queue = useRef([]);
  const lastPage = useRef(null);

  const lastActivity = useRef(Date.now());
  const IDLE_TIMEOUT = 3 * 60 * 1000; // 3 minutos

  // Activity tracking (mouse, touch, scroll)
  useEffect(() => {
    const updateActivity = () => {
      lastActivity.current = Date.now();
    };
    const opts = { passive: true };
    window.addEventListener("scroll", updateActivity, opts);
    window.addEventListener("touchstart", updateActivity, opts);
    window.addEventListener("mousemove", updateActivity, opts);
    window.addEventListener("keydown", updateActivity, opts);
    window.addEventListener("click", updateActivity, opts);

    return () => {
      window.removeEventListener("scroll", updateActivity);
      window.removeEventListener("touchstart", updateActivity);
      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("keydown", updateActivity);
      window.removeEventListener("click", updateActivity);
    };
  }, []);

  const enqueue = useCallback(
    (event_type, extra = {}) => {
      if (!token) return;
      queue.current.push({
        session_id: sessionId.current,
        event_type,
        page: extra.page || location.pathname,
        metadata: extra.metadata || null,
        duration_seconds: extra.duration_seconds || null,
      });
    },
    [token, location.pathname]
  );

  const flush = useCallback(async () => {
    if (!token || queue.current.length === 0) return;
    const batch = queue.current.splice(0);
    try {
      await instance.post("/admin/events/batch", { events: batch });
    } catch (err) {
      queue.current.unshift(...batch);
    }
  }, [token]);

  // Session start
  useEffect(() => {
    if (!token) return;
    enqueue("session_start", {
      metadata: getDeviceInfo(),
    });
    const startRef = sessionStart.current;
    return () => {
      const dur = Math.round((Date.now() - startRef) / 1000);
      enqueue("session_end", { duration_seconds: dur });
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Page view on route change
  useEffect(() => {
    if (!token) return;
    const path = location.pathname;
    if (path !== lastPage.current) {
      lastPage.current = path;
      enqueue("page_view", { page: path });
    }
  }, [location.pathname, token, enqueue]);

  // Heartbeat — so we know the user is still active
  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => {
      // Se a aba estiver oculta/minimizada, não manda heartbeat
      if (document.hidden) return;
      
      // Se estiver inativo por mais de 3 minutos (sem touch, scroll, etc), não manda heartbeat
      if (Date.now() - lastActivity.current > IDLE_TIMEOUT) return;

      enqueue("heartbeat");
    }, HEARTBEAT_INTERVAL);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, enqueue]);

  // Periodic flush of queued events
  useEffect(() => {
    if (!token) return;
    const id = setInterval(flush, BATCH_FLUSH_INTERVAL);
    return () => {
      clearInterval(id);
      flush();
    };
  }, [token, flush]);

  // Flush on tab close / visibility change (pagehide: Safari/iOS costuma não disparar beforeunload)
  useEffect(() => {
    if (!token) return;

    let unloadEndSent = false;
    const sendSessionEndUnloading = () => {
      if (unloadEndSent) return;
      unloadEndSent = true;
      const dur = Math.round((Date.now() - sessionStart.current) / 1000);
      const payload = {
        events: [
          ...queue.current.splice(0),
          {
            session_id: sessionId.current,
            event_type: "session_end",
            page: location.pathname,
            metadata: null,
            duration_seconds: dur,
          },
        ],
      };
      // sendBeacon can't send custom headers, so we use fetch keepalive
      const url = `${instance.defaults.baseURL}/admin/events/batch`;
      try {
        fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
          keepalive: true,
        });
      } catch {
        const blob = new Blob([JSON.stringify(payload)], {
          type: "application/json",
        });
        navigator.sendBeacon?.(url, blob);
      }
    };

    const handleBeforeUnload = () => sendSessionEndUnloading();
    const handlePageHide = () => sendSessionEndUnloading();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flush();
      } else {
        // Voltou para o app, atualiza a atividade
        lastActivity.current = Date.now();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token, location.pathname, flush]);

  return { trackEvent: enqueue };
}
